import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import Checkbox from "components/base/Checkbox";
import { JobsContext } from "context/providers";
import { getGeographicalRegionsFilterDescription } from "utils/helpers/locationFilter";

const DEFAULT_RADIUS_MILES = 50;

const DISALLOWED_RADIUS_NUMBER_KEYS = new Set(["-", "+", "e", "E", ".", ","]);

function sanitizeRadiusMilesInput(value) {
  const withoutMinus = value.replace(/-/g, "");
  if (withoutMinus === "") {
    return "";
  }
  const n = parseInt(withoutMinus, 10);
  if (!Number.isNaN(n)) {
    return String(Math.trunc(Math.abs(n)));
  }
  return withoutMinus.replace(/\D/g, "");
}

const WebAppliesLocationFilter = ({ onFilterChange, isFiltering }) => {
  const { jobOpp } = useContext(JobsContext);
  const [radiusMiles, setRadiusMiles] = useState(DEFAULT_RADIUS_MILES);
  const [radiusInput, setRadiusInput] = useState(String(DEFAULT_RADIUS_MILES));

  const hasSpecificLocation =
    jobOpp?.location?.latitude != null && jobOpp?.location?.longitude != null;
  const hasGeographicalRegions =
    !hasSpecificLocation &&
    jobOpp?.geographicalRegions &&
    jobOpp.geographicalRegions.length > 0;

  // Don't render if job has neither location type
  if (!hasSpecificLocation && !hasGeographicalRegions) {
    return null;
  }

  const handleCheckboxChange = (checked) => {
    onFilterChange(checked, radiusMiles);
  };

  const applyRadiusIfValid = (raw, notifyParent) => {
    const n = parseInt(raw, 10);
    if (!Number.isNaN(n) && n > 0) {
      setRadiusMiles(n);
      if (notifyParent && isFiltering) {
        onFilterChange(true, n);
      }
      return true;
    }
    return false;
  };

  const handleRadiusKeyDown = (e) => {
    if (DISALLOWED_RADIUS_NUMBER_KEYS.has(e.key)) {
      e.preventDefault();
    }
  };

  const handleRadiusChange = (e) => {
    const raw = sanitizeRadiusMilesInput(e.target.value);
    setRadiusInput(raw);
    applyRadiusIfValid(raw, true);
  };

  const handleRadiusBlur = () => {
    if (applyRadiusIfValid(radiusInput, true)) {
      setRadiusInput(String(parseInt(radiusInput, 10)));
      return;
    }
    setRadiusInput(String(radiusMiles));
  };

  const getLocationDescription = () => {
    if (hasSpecificLocation) {
      const { cityName, stateName, countryName } = jobOpp.location;
      const locationParts = [cityName, stateName, countryName].filter(Boolean);
      return locationParts.join(", ");
    }

    if (hasGeographicalRegions) {
      return getGeographicalRegionsFilterDescription(
        jobOpp.geographicalRegions
      );
    }

    return "";
  };

  return (
    <div className="flex flex-col gap-2 p-3 border rounded-md bg-white">
      <div className="flex items-center gap-4">
        <Checkbox
          label="Filter by Location"
          className="mt-0"
          checked={isFiltering}
          onChange={handleCheckboxChange}
        />

        {hasSpecificLocation && isFiltering && (
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">
              Radius (miles):
            </label>
            <input
              type="number"
              min={1}
              step={1}
              aria-label="Radius in miles"
              value={radiusInput}
              onKeyDown={handleRadiusKeyDown}
              onChange={handleRadiusChange}
              onBlur={handleRadiusBlur}
              className="w-24 px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        )}
      </div>

      {isFiltering && (
        <div className="text-sm text-gray-600">
          {hasSpecificLocation ? (
            <p>
              Showing candidates within <strong>{radiusMiles} miles</strong> of{" "}
              <strong>{getLocationDescription()}</strong>
            </p>
          ) : (
            hasGeographicalRegions && (
              <p>
                Showing candidates in:{" "}
                <strong>{getLocationDescription()}</strong>
              </p>
            )
          )}
        </div>
      )}
    </div>
  );
};

WebAppliesLocationFilter.propTypes = {
  onFilterChange: PropTypes.func.isRequired,
  isFiltering: PropTypes.bool.isRequired,
};

export default WebAppliesLocationFilter;
